/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import FeatherIcon from 'feather-icons-react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Button from 'components/base/Button';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import returnItemService from 'service/returnItemService';
import returnReasonService from 'service/returnReason';

export type NewReturningInfo = {
  product_name: string;
  barcode: string;
  unit: string;
  currency: string;
  amount: string;
  return_reason_id: string;
  current_balance: string;
  measure_unit_id: string;
  package_quantity: any;
  measure_unit_kind_pid: number;
  invoice_pid: any;
};

interface NewReturningTableProps {
  returnItemData?: any;
  newReturningId?: any;
  setReturnItemData?: any;
  setRef?: any;
  focusRef?: any;
  setSavedBarcodes?: any;
  selectedReasonId?: any;
}

const NewReturningTable = ({
  returnItemData,
  setReturnItemData,
  setRef,
  focusRef,
  selectedReasonId
}: NewReturningTableProps) => {
  const { t, i18n } = useTranslation();
  const { state } = useLocation();
  const { newreturning_pid } = state || {};

  const [returnReasonOptionSingle, setReturnReasonOptionSingle] = useState<any>(
    []
  );
  //GET return Reason  to options
  useQuery(['GET_RETURN_REASON'], async () => {
    await returnReasonService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.product_return_reasons?.map((option: any) => ({
          value: option.id,
          pid: option?.pid,
          label: option.name?.[i18n?.language]
        }));

        setReturnReasonOptionSingle(options);
      });
  });

  const { data } = useQuery(
    ['GET_RETURN_ITEMS', newreturning_pid],
    async () => {
      if (newreturning_pid) {
        const res = await returnItemService
          .getReturnItemOutById(newreturning_pid, {
            limit: 100,
            offset: 0
          })
          .then((res: any) => {
            return res?.return_items;
          });
        return res;
      }
    },
    { enabled: !!newreturning_pid }
  );

  const generateData: NewReturningInfo[] = useMemo(() => {
    const newData =
      data?.map((items: any) => {
        const packageQuantity =
          items?.package_quantity !== undefined ? items.package_quantity : 1;

        const quantity = items?.quantity
          ? Number(items?.quantity.toFixed(3))
          : 0;
        const totalQuantity = items?.total_quantity
          ? Number(items?.total_quantity.toFixed(3))
          : 0;

        const fullPackages = Math.floor(quantity / packageQuantity);
        const remainingQuantity = Math.floor(quantity % packageQuantity);
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        const fullPackagesTotal = Math.floor(totalQuantity / packageQuantity);
        const remainingQuantityTotal = totalQuantity % packageQuantity;
        const totalQuantityDisplay = `${fullPackagesTotal}/${remainingQuantityTotal}`;

        const selectedReason = returnReasonOptionSingle.find(
          (reason: any) => reason.value === selectedReasonId
        );

        return {
          product_name: items?.product_short_name,
          barcode: items?.barcode,
          unit: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
          measure_unit_id: items?.nomenclature?.measure_unit.id,
          measure_unit_pid: items?.nomenclature?.measure_unit.pid || 0,
          amount:
            items?.nomenclature?.measure_unit_kind_pid === 1
              ? quantityDisplay
              : items?.quantity ?? 0,
          full_cost: items?.price_wholesale,
          currency: items?.currency?.name?.[i18n?.language],
          return_reason_id: selectedReasonId || items?.return_reason_id,
          return_reason_pid:
            selectedReason?.pid || items?.return_reason_pid || 0,
          nomenclature_id: items?.nomenclature_id,
          nomenclature_pid: items?.nomenclature_pid || 0,
          product_id: items?.product_id,
          product_pid: items?.product_pid || 0,
          parent_product_id: items?.parent_product_id,
          parent_product_pid: items?.parent_product_pid || 0,
          price: items?.price,
          is_vat: items?.is_vat ? items?.is_vat : false,
          currency_id: items?.currency_id,
          currency_pid: items?.currency_pid || 0,
          package_quantity:
            items?.package_quantity !== undefined ? items.package_quantity : 1,
          measure_unit_kind_id: items?.nomenclature?.measure_unit_kind_id,
          measure_unit_kind_pid:
            items?.nomenclature?.measure_unit_kind_pid || 0,
          /* prettier-ignore */
          current_balance:
            items?.nomenclature?.measure_unit_kind_pid ===
                  1
                    ? totalQuantityDisplay
                    : items?.total_quantity
                      ? new Intl.NumberFormat('en-US', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 4,
                          useGrouping: true
                        })
                          .format(items?.total_quantity)
                          .replace(/,/g, ' ')
                      : 0,
          invoice_pid: items?.invoice_pid,
          order_pid: items?.order_pid || 0,
          order_item_pid: items?.order_item_pid || 0,
          invoice_payment_order_pid: items?.payment_order?.pid || 0
        };
      }) ?? [];

    setReturnItemData([...returnItemData, ...newData]);

    return newData;
  }, [data, t, i18n?.language]);

  console.log(generateData);

  const handleRemoveProductItem = (indexToRemove: number) => {
    setReturnItemData(
      returnItemData.filter((_: any, index: any) => index !== indexToRemove)
    );
  };

  const newReturningDataTableColumns: ColumnDef<NewReturningInfo>[] = [
    {
      id: 'action',
      cell: rowData => (
        <div>
          <Button
            variant="hover"
            onClick={() => handleRemoveProductItem(+rowData?.row?.id)}
          >
            <FeatherIcon
              icon="trash-2"
              className="cursor-pointer text-danger"
              size={20}
            />
          </Button>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '7%' } }
      }
    },

    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'unit',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'total_price',
      header: t('purchase_price'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },

    {
      accessorKey: 'current_balance',
      header: t('current_balance'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'amount',
      header: t('quantity'),
      cell: rowData => {
        const amount: any = rowData.getValue() ?? '1';
        const { measure_unit_kind_pid, current_balance } = rowData.row.original;
        const packageQuantity = rowData?.row?.original?.package_quantity || 1;
        /* prettier-ignore */
        const formatWithSpaces = (val: any) => {
          const cleanValue = String(val || '').replace(/[^0-9,./]/g, ''); // Remove non-numeric chars except ,./
          if (!cleanValue) return '';
          const [integerPart, decimalPart] = cleanValue.includes('.')
            ? cleanValue.split('.')
            : cleanValue.includes('/')
              ? cleanValue.split('/')
              : [cleanValue, ''];
          const formattedInteger = integerPart.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ' '
          );
          return decimalPart
            ? `${formattedInteger}${cleanValue.includes('.') ? '.' : '/'}${decimalPart}`
            : formattedInteger;
        };

        const removeSpaces = (val: string) => {
          return val.replace(/\s/g, '');
        };

        const [value, setValue] = useState(
          measure_unit_kind_pid === 1
            ? String(amount)
            : formatWithSpaces(String(amount))
        );

        let integerPartRemainder = 0;
        let numeratorRemainder = 0;
        let denominatorRemainder = 0;

        const quantityStrRemainder = String(current_balance);

        if (quantityStrRemainder?.includes('/')) {
          [numeratorRemainder, denominatorRemainder] = quantityStrRemainder
            .split('/')
            .map(Number);
          integerPartRemainder = numeratorRemainder;
        } else {
          integerPartRemainder = +quantityStrRemainder;
        }

        const adjustQuantityRemainder =
          measure_unit_kind_pid === 1
            ? integerPartRemainder * packageQuantity + denominatorRemainder
            : integerPartRemainder;

        const calculateAdjustedQuantity = (quantity: string) => {
          let integerPart = 0;
          let numerator = 0;
          let denominator = 0;

          const quantityStr = String(quantity);

          if (quantityStr?.includes('/')) {
            [numerator, denominator] = quantityStr.split('/').map(Number);
            integerPart = numerator;
          } else {
            integerPart = +quantityStr;
          }

          return measure_unit_kind_pid === 1
            ? integerPart * packageQuantity + denominator
            : integerPart;
        };

        const onBlur = (e: React.ChangeEvent<HTMLInputElement>, index: any) => {
          const { name, value } = e.target;
          const cleanValue = removeSpaces(value); // Remove spaces for state
          const formattedValue = cleanValue.replace(/[^0-9,./]/g, ''); // Strip invalid characters

          const adjustedQuantity = calculateAdjustedQuantity(formattedValue);

          if (+adjustedQuantity <= +adjustQuantityRemainder) {
            const updatedItems = returnItemData.map((item: any, i: any) => {
              if (i === +index) {
                return { ...item, [name]: formattedValue, adjustedQuantity };
              }
              return item;
            });

            setReturnItemData(updatedItems);
            setValue(
              measure_unit_kind_pid === 1
                ? formattedValue
                : formatWithSpaces(formattedValue)
            );
          } else {
            setValue(current_balance);
          }
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;

          // const numberPattern = /^-?\d*\.?\d*$/;
          // const remainderPattern = /^-?\d*\/?\d*$/;

          const integerPattern = /^-?\d*$/;
          const fractionPattern = /^-?\d*\/?\d*$/;
          const decimalPattern = /^-?\d*[.,]?\d*$/;

          if (measure_unit_kind_pid === 2) {
            if (
              integerPattern.test(newValue) &&
              !newValue.includes('.') &&
              !newValue.includes(',') &&
              !newValue.includes('/')
            ) {
              setValue(newValue);
            }
          } else if (measure_unit_kind_pid === 1) {
            if (
              fractionPattern.test(newValue) &&
              !newValue.includes('.') &&
              !newValue.includes(',')
            ) {
              setValue(newValue);
            }
          } else {
            if (decimalPattern.test(newValue)) {
              setValue(newValue);
            }
          }
        };

        const updateProductInvoiceData = (
          name: string,
          value: string,
          index: any
        ) => {
          const updatedItems = returnItemData.map((item: any, i: any) => {
            if (i === +index) {
              return { ...item, [name]: value };
            }
            return item;
          });
          setReturnItemData(updatedItems);
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            const { name, value } = e.target as HTMLInputElement;
            try {
              updateProductInvoiceData(name, value, rowData.row.index);
            } catch (error) {
              console.log('error===>', error);
            }
            const currentRowIndex = rowData.row.index;
            const nextRowIndex = currentRowIndex + 1;

            if (nextRowIndex < returnItemData?.length) {
              setTimeout(() => {
                focusRef(`amount_${nextRowIndex}`);
              }, 500);
            } else {
              setTimeout(() => {
                focusRef(`amount_0`);
              }, 500);
            }
          }
        };

        return (
          <div>
            <Form.Group>
              <Form.Control
                type="text"
                name="amount"
                value={value}
                onChange={handleChange}
                onBlur={(e: any) => onBlur(e, rowData.row?.index)}
                onFocus={e => e.target.select()}
                onKeyDown={handleKeyDown}
                ref={setRef(`amount_${rowData.row.index}`)}
                style={{ textAlign: 'right' }}
              />
            </Form.Group>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '10%' } },
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'return_reason_id',
      header: t('reason_return'),
      cell: rowData => {
        return (
          <div>
            <Form.Group className="md-4">
              <div className="react-select-container">
                <Form.Group>
                  <Form.Select
                    value={
                      rowData?.row?.original?.return_reason_id ||
                      selectedReasonId
                    }
                    onChange={(e: any) => {
                      const selectedOption = returnReasonOptionSingle.find(
                        (option: any) => option.value === e.target.value
                      );
                      if (selectedOption) {
                        const updatedItems = returnItemData.map(
                          (item: any, i: any) => {
                            if (i === +rowData?.row?.id) {
                              return {
                                ...item,
                                return_reason_id: selectedOption.value,
                                return_reason_pid: selectedOption.pid
                              };
                            }
                            return item;
                          }
                        );
                        setReturnItemData(updatedItems);
                      }
                    }}
                  >
                    <option className="d-none" value=""></option>
                    {returnReasonOptionSingle?.map((option: any) => (
                      <option
                        key={option.value}
                        value={option.value}
                        className="option"
                      >
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </Form.Group>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'date_order',
      header: t('purchase_date'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: returnItemData,
    columns: newReturningDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>
    </div>
  );
};

export default NewReturningTable;
